import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Star, Quote, ArrowRight, Users, Award, MessageCircle } from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

const Reviews = () => {
  const reviews = [
    {
      name: "Sarah Mitchell",
      location: "Austin, TX",
      rating: 5,
      title: "Finally, life insurance that makes sense!",
      review: "I've been putting off getting life insurance for years because the process seemed so overwhelming. Safurra made it incredibly simple - I had my quote in under 5 minutes and was approved the next day. No pushy salespeople, just honest recommendations.",
      coverage: "$750k Term Life",
      saved: "$180/month vs other quotes"
    },
    {
      name: "Michael Rodriguez",
      location: "Phoenix, AZ", 
      rating: 5,
      title: "Saved me hundreds compared to my agent",
      review: "My insurance agent wanted to charge me $350/month for $500k coverage. Safurra found me the same coverage for $180/month with an A+ rated carrier. The AI actually understood my health situation better than the human agents I talked to.",
      coverage: "$500k Term Life",
      saved: "$170/month vs agent quote"
    },
    {
      name: "Jennifer Chen",
      location: "Seattle, WA",
      rating: 5,
      title: "Perfect for busy parents",
      review: "As a working mom, I needed something fast and reliable. The whole process took 15 minutes during my lunch break. No medical exam required, and I was covered immediately. This is how insurance should be done in 2024.",
      coverage: "$1M Term Life",
      saved: "No medical exam required"
    },
    {
      name: "David Thompson", 
      location: "Miami, FL",
      rating: 5,
      title: "Transparent pricing, no surprises",
      review: "What I loved most was the transparency. Every step was clearly explained, the pricing was upfront, and there were no hidden fees or surprise calls later. Got exactly what was promised at the price quoted.",
      coverage: "$300k Whole Life",
      saved: "Locked-in premium guarantee"
    },
    {
      name: "Amanda Foster",
      location: "Denver, CO",
      rating: 5,
      title: "Great for self-employed professionals",
      review: "Being self-employed, I needed affordable coverage without the hassle of traditional underwriting. Safurra's platform understood my variable income and found me flexible premium options. Highly recommend for freelancers and entrepreneurs.",
      coverage: "$600k Term Life",
      saved: "Flexible premium payments"
    },
    {
      name: "Robert Kim",
      location: "Portland, OR",
      rating: 5,
      title: "Excellent customer support",
      review: "Had a few questions during the application process and their support team was incredibly helpful. Real people who actually understand insurance, not just call center scripts. Made me feel confident in my decision.",
      coverage: "$400k Term Life",
      saved: "Personal guidance included"
    }
  ];

  const stats = [
    {
      icon: Users,
      number: "50,000+",
      label: "Happy Customers",
      description: "Families protected nationwide"
    },
    {
      icon: Star,
      number: "4.9/5",
      label: "Average Rating",
      description: "Based on customer reviews"
    },
    {
      icon: Award,
      number: "98%",
      label: "Satisfaction Rate",
      description: "Would recommend to friends"
    },
    {
      icon: MessageCircle,
      number: "<24hrs",
      label: "Response Time",
      description: "Customer support availability"
    }
  ];

  const trustPilotReviews = [
    { rating: 5, text: "Exactly what I needed - simple and honest", author: "K. Williams" },
    { rating: 5, text: "Beat my agent's quote by $200/month", author: "M. Johnson" },
    { rating: 5, text: "No pressure, just great recommendations", author: "L. Davis" },
    { rating: 5, text: "Applied during lunch break, approved same day", author: "T. Brown" }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-b from-primary-light/30 to-background">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-6xl font-bold text-foreground mb-6">
              Customer Reviews
            </h1>
            <p className="text-xl md:text-2xl text-muted-foreground mb-8 max-w-3xl mx-auto">
              See what real customers say about their experience finding life insurance through Safurra.
            </p>
            <div className="flex items-center justify-center space-x-2 mb-8">
              <div className="flex">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-6 w-6 text-yellow-500 fill-current" />
                ))}
              </div>
              <span className="text-lg font-semibold text-foreground">4.9/5</span>
              <span className="text-muted-foreground">from 2,300+ reviews</span>
            </div>
            <Link to="/">
              <Button size="lg" variant="hero">
                Join Thousands of Happy Customers
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {stats.map((stat, index) => (
                <Card key={index} className="text-center p-6 hover:shadow-lg transition-shadow">
                  <CardContent className="p-0">
                    <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                      <stat.icon className="h-6 w-6 text-primary" />
                    </div>
                    <div className="text-2xl md:text-3xl font-bold text-primary mb-1">
                      {stat.number}
                    </div>
                    <div className="font-medium text-foreground mb-1">
                      {stat.label}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {stat.description}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Featured Reviews */}
      <section className="py-20 bg-primary-light/30">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-6">
                What Our Customers Say
              </h2>
              <p className="text-xl text-muted-foreground">
                Real stories from real customers who found the perfect coverage through Safurra.
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {reviews.map((review, index) => (
                <Card key={index} className="h-full hover:shadow-lg transition-shadow">
                  <CardContent className="p-6 h-full flex flex-col">
                    {/* Rating */}
                    <div className="flex items-center mb-4">
                      <div className="flex mr-3">
                        {[...Array(review.rating)].map((_, i) => (
                          <Star key={i} className="h-4 w-4 text-yellow-500 fill-current" />
                        ))}
                      </div>
                      <span className="text-sm font-medium text-foreground">
                        {review.rating}.0
                      </span>
                    </div>
                    
                    {/* Title */}
                    <h3 className="font-semibold text-foreground mb-3">
                      {review.title}
                    </h3>
                    
                    {/* Review Text */}
                    <div className="flex-grow mb-4">
                      <Quote className="h-6 w-6 text-primary/30 mb-2" />
                      <p className="text-muted-foreground text-sm leading-relaxed">
                        {review.review}
                      </p>
                    </div>
                    
                    {/* Coverage Info */}
                    <div className="bg-primary/5 rounded-lg p-3 mb-4 text-sm">
                      <div className="font-medium text-foreground mb-1">
                        Coverage: {review.coverage}
                      </div>
                      <div className="text-primary font-medium">
                        {review.saved}
                      </div>
                    </div>
                    
                    {/* Author */}
                    <div className="flex items-center">
                      <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center mr-3">
                        <span className="text-primary font-medium text-sm">
                          {review.name.split(' ').map(n => n[0]).join('')}
                        </span>
                      </div>
                      <div>
                        <div className="font-medium text-foreground text-sm">
                          {review.name}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {review.location}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Trust Pilot Reviews */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-6">
                Recent Reviews
              </h2>
              <p className="text-xl text-muted-foreground">
                Latest feedback from our customers
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-6">
              {trustPilotReviews.map((review, index) => (
                <Card key={index} className="p-6">
                  <CardContent className="p-0">
                    <div className="flex items-center mb-3">
                      <div className="flex mr-3">
                        {[...Array(review.rating)].map((_, i) => (
                          <Star key={i} className="h-4 w-4 text-yellow-500 fill-current" />
                        ))}
                      </div>
                      <span className="text-sm text-muted-foreground">
                        - {review.author}
                      </span>
                    </div>
                    <p className="text-foreground">
                      "{review.text}"
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-primary-light/30">
        <div className="container mx-auto px-6">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-6">
              Ready to Join Our Happy Customers?
            </h2>
            <p className="text-xl text-muted-foreground mb-8">
              Experience the same simple, transparent process that thousands of families have trusted.
            </p>
            <Link to="/">
              <Button size="lg" variant="hero">
                Get Your Quote Now
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Reviews;